import { useApolloClient } from '@apollo/react-hooks'
import { gql } from 'apollo-boost'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from '@paintscout/ui'
import { contentHash } from '@paintscout/util/builder'
import { useQuote } from '../../../context'
import { getCachedTranslation, setCachedTranslation } from './useTranslatedNoteContent.cache'

// Augment here to guarantee visibility in all webpack/fork-ts-checker
// compilation contexts (symlinked @types packages can be missed by fork-ts-checker v6).
declare module 'paintscout' {
  export interface QuoteDocument {
    translations?: Record<string, Record<string, string>>
  }
}

const TRANSLATE_NOTE_CONTENT = gql`
  query translateNoteContent($notes: [String!]!, $lang: String!) {
    translateNoteContent(notes: $notes, lang: $lang)
  }
`

const EMPTY_TRANSLATIONS: Record<string, string> = {}
const HAS_ALPHA_CHARACTER = /[a-z]/i

// Tracks how many useTranslatedNoteContent instances are actively fetching translations.
// Written to document.body so the PDF pdfReady poll can gate on it.
let translationLoadingCount = 0

function incrementTranslationLoading() {
  translationLoadingCount++
  if (typeof document !== 'undefined') {
    document.body.dataset.translationsLoading = 'true'
  }
}

function decrementTranslationLoading() {
  translationLoadingCount = Math.max(0, translationLoadingCount - 1)
  if (translationLoadingCount === 0 && typeof document !== 'undefined') {
    document.body.dataset.translationsLoading = 'false'
  }
}

export function useTranslatedNoteContent(contents: Array<string | null | undefined>) {
  const apolloClient = useApolloClient()
  const { lang } = useTranslation()
  const { quote } = useQuote()
  // The pdf flag comes from the request URL and never changes during a render session,
  // so parse it once instead of on every hook call (a work order mounts this hook for
  // every note field).
  const isPdfRender = useMemo(
    () => typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('pdf') === '1',
    []
  )

  const normalizedContents = contents.map((content) => content ?? '')
  const normalizedContentsKey = normalizedContents.join('\u0001')
  const preBakedTranslations = useMemo(
    () => quote?.translations?.[lang] ?? EMPTY_TRANSLATIONS,
    [lang, quote?.translations]
  )
  const preBakedTranslationsKey = normalizedContents
    .map((content) => preBakedTranslations[contentHash(content)] ?? '')
    .join('\u0001')
  const initialTranslatedContents = useMemo(() => {
    seedTranslationCache({ lang, normalizedContents, preBaked: preBakedTranslations })
    return getVisibleContents({
      lang,
      normalizedContents,
      preBaked: preBakedTranslations,
      hideUncached: !isPdfRender
    })
  }, [isPdfRender, lang, normalizedContentsKey, preBakedTranslationsKey])
  const [translatedContents, setTranslatedContents] = useState<string[]>(initialTranslatedContents)
  // Set to true in the async translate() finally block, cleared in the post-render
  // useEffect below. This ensures decrementTranslationLoading() is called only after
  // React has committed translated content to the DOM (Skeletons gone), so pdfReady
  // does not fire while loading bars are still visible.
  const pendingDecrementRef = useRef(false)

  const loading = useMemo(() => {
    if (!lang || lang === 'en') return false
    if (isPdfRender) return false
    return normalizedContents.some((content, i) => {
      if (!content.trim() || !HAS_ALPHA_CHARACTER.test(content)) return false
      return translatedContents[i] === ''
    })
  }, [isPdfRender, lang, normalizedContentsKey, translatedContents])

  useEffect(() => {
    setTranslatedContents((current) =>
      areContentsEqual(current, initialTranslatedContents) ? current : initialTranslatedContents
    )

    if (!lang || lang === 'en' || isPdfRender) {
      return
    }

    const uniqueContents = Array.from(
      new Set(normalizedContents.filter((content) => content.trim() && HAS_ALPHA_CHARACTER.test(content)))
    )

    const uncached = uniqueContents.filter((content) => !getCachedTranslation(getCacheKey(lang, content)))
    let cancelled = false
    // Track whether we incremented so the cleanup and the finally can both
    // guard against double-decrement — whichever fires first wins.
    let translationPending = uncached.length > 0

    if (translationPending) incrementTranslationLoading()

    async function translate() {
      try {
        if (uncached.length) {
          const { data } = await apolloClient.query<{ translateNoteContent: string[] }>({
            query: TRANSLATE_NOTE_CONTENT,
            variables: {
              notes: uncached,
              lang
            },
            fetchPolicy: 'no-cache'
          })

          const translated = data?.translateNoteContent ?? uncached
          uncached.forEach((content, index) => {
            setCachedTranslation(getCacheKey(lang, content), translated[index] ?? content)
          })
        }
      } catch {
        uncached.forEach((content) => {
          setCachedTranslation(getCacheKey(lang, content), content)
        })
      } finally {
        // Mark for a post-render decrement rather than decrementing immediately.
        // Decrementing here (before setTranslatedContents triggers a re-render)
        // would clear data-translations-loading while Skeleton loaders are still
        // in the DOM, letting pdfReady fire too early.
        if (translationPending) {
          translationPending = false
          pendingDecrementRef.current = true
        }
      }

      if (cancelled) {
        return
      }

      setTranslatedContents(
        normalizedContents.map((content) => getCachedTranslation(getCacheKey(lang, content)) ?? content)
      )
    }

    void translate()

    return () => {
      cancelled = true
      // If the async query is still in-flight when this effect is torn down,
      // decrement immediately so pdfReady isn't blocked after unmount.
      if (translationPending) {
        translationPending = false
        decrementTranslationLoading()
      }
      // If translate() already finished but the post-render effect hasn't fired
      // yet (e.g. component unmounted between finally and the next paint), clear
      // the pending decrement here to avoid a double-decrement in the effect.
      if (pendingDecrementRef.current) {
        pendingDecrementRef.current = false
        decrementTranslationLoading()
      }
    }
  }, [apolloClient, isPdfRender, lang, normalizedContentsKey, preBakedTranslationsKey])

  // Fires after every commit. When translate() has finished and React has painted
  // the translated content (Skeletons gone), flush the pending decrement so
  // data-translations-loading clears only once the DOM reflects translated text.
  useEffect(() => {
    if (pendingDecrementRef.current) {
      pendingDecrementRef.current = false
      decrementTranslationLoading()
    }
  })

  return [translatedContents, loading] as [string[], boolean]
}

function getCacheKey(lang: string, content: string) {
  return `${lang}::${content}`
}

function seedTranslationCache({
  lang,
  normalizedContents,
  preBaked
}: {
  lang?: string
  normalizedContents: string[]
  preBaked: Record<string, string>
}) {
  if (!lang || lang === 'en') {
    return
  }

  normalizedContents
    .filter((content) => content.trim() && HAS_ALPHA_CHARACTER.test(content))
    .forEach((content) => {
      const key = getCacheKey(lang, content)
      if (!getCachedTranslation(key)) {
        const preBakedValue = preBaked[contentHash(content)]
        if (preBakedValue) {
          setCachedTranslation(key, preBakedValue)
        }
      }
    })
}

function getVisibleContents({
  lang,
  normalizedContents,
  preBaked,
  hideUncached
}: {
  lang?: string
  normalizedContents: string[]
  preBaked: Record<string, string>
  hideUncached: boolean
}) {
  if (!lang || lang === 'en') {
    return normalizedContents
  }

  return normalizedContents.map((content) => {
    if (!content.trim() || !HAS_ALPHA_CHARACTER.test(content)) {
      return content
    }

    const cached = getCachedTranslation(getCacheKey(lang, content))
    if (cached != null) {
      return cached
    }

    const preBakedValue = preBaked[contentHash(content)]
    if (preBakedValue != null) {
      return preBakedValue
    }

    return hideUncached ? '' : content
  })
}

function areContentsEqual(left: string[], right: string[]) {
  return left.length === right.length && left.every((value, index) => value === right[index])
}
